/**
 * offlineQueue — a durable IndexedDB "outbox" for mutations made while offline.
 *
 * Why an explicit outbox (not workbox BackgroundSync):
 *   The axios client attaches a FRESH JWT (and refreshes on 401) on every
 *   request. Replaying queued operations through that client — hours later,
 *   after the worker drives back into signal — therefore always carries a valid
 *   token. Workbox BackgroundSync replays the raw fetch with its original
 *   (now-stale) Authorization header and gives no UI feedback, so it's the wrong
 *   fit here. We store the operation INTENT (kind + payload), not a raw request,
 *   and re-dispatch it through the normal API layer (see offlineSync.ts).
 *
 * This module is storage-only: no API imports (keeps it cycle-free and testable).
 */

export type QueuedKind = 'harvest' | 'activity' | 'observation'

export interface QueuedOp {
  id: string                       // client-generated uuid (also dedupes replays)
  kind: QueuedKind
  payload: unknown                 // the request body; re-dispatched by kind in offlineSync
  label: string                    // human summary for the sync UI ("Harvest · 12 lb")
  createdAt: number
  status: 'pending' | 'syncing' | 'failed'
  attempts: number
  error?: string
}

const DB_NAME = 'grafty-offline'
const DB_VERSION = 1
const STORE = 'outbox'

let dbPromise: Promise<IDBDatabase> | null = null

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode)
        const req = fn(t.objectStore(STORE))
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
      }),
  )
}

// ── Change subscription (drives the React sync indicator) ──────────────────────
type Listener = () => void
const listeners = new Set<Listener>()
export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}
function emit() {
  listeners.forEach((fn) => {
    try { fn() } catch { /* listener errors must not break the queue */ }
  })
}

// ── CRUD ───────────────────────────────────────────────────────────────────────
export async function enqueue(input: Pick<QueuedOp, 'kind' | 'payload' | 'label'>): Promise<QueuedOp> {
  const op: QueuedOp = {
    id: (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`),
    createdAt: Date.now(),
    status: 'pending',
    attempts: 0,
    ...input,
  }
  await tx('readwrite', (s) => s.put(op))
  emit()
  return op
}

export async function allOps(): Promise<QueuedOp[]> {
  const ops = (await tx<QueuedOp[]>('readonly', (s) => s.getAll())) ?? []
  return ops.sort((a, b) => a.createdAt - b.createdAt)
}

export async function updateOp(id: string, patch: Partial<QueuedOp>): Promise<void> {
  const existing = await tx<QueuedOp | undefined>('readonly', (s) => s.get(id))
  if (!existing) return
  await tx('readwrite', (s) => s.put({ ...existing, ...patch, id }))
  emit()
}

export async function removeOp(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id))
  emit()
}

// Every op in the store is unsynced (successful ops are removed), so the count
// of outstanding work is simply the store size.
export async function pendingCount(): Promise<number> {
  return (await allOps()).length
}
